// ID 11 · ENFERMERÍA INSTITUCIONAL — Habitaciones: censo por cama, ingreso, traslado, egreso/reingreso
// y comprobantes. La autoridad es el backend (`/api/sitios/{id}/censo|movimientos`): esta vista no
// decide permisos, ocupación ni efectos; sólo muestra lo que el servidor resolvió y pide confirmación.
// Una confirmación usa UNA clave de idempotencia por intento: reenviar tras una respuesta perdida
// repite la MISMA operación (sin otra persona, estancia ni folio).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { descargarReporte, imprimirReporte } from "@/lib/reportes";
import { fmtFecha } from "@/lib/format";
import { BedDouble, LogIn, ArrowRightLeft, LogOut, FileDown, Printer, AlertTriangle, RefreshCw } from "lucide-react";

const nuevaClave = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `mov-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const TITULO = { ingreso: "Ingreso", traslado: "Traslado", egreso: "Egreso" };

const codigoError = (e) => {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" ? d.codigo : null;
};
// Conflictos que significan «la pantalla está vieja»: se refresca la fila y se vuelve a revisar.
const CONFLICTO_PANTALLA = new Set(["version_desactualizada", "estado_cambio", "cama_ocupada",
  "movimiento_en_curso", "version_requerida"]);

function detalleError(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.mensaje) return d.mensaje;
  return formatApiErrorDetail(d) || "No se pudo completar la operación.";
}

function Comprobante({ sitioId, mov }) {
  if (!mov?.folio || mov.estado !== "aplicado") return null;
  const path = `/sitios/${sitioId}/movimientos/${mov.id}/comprobante`;
  return (
    <span className="inline-flex gap-1.5">
      <Button size="sm" variant="outline" data-testid={`mov-comprobante-${mov.id}`}
        onClick={() => descargarReporte(path, { tamano: "carta" }, `${mov.folio}.pdf`)}>
        <FileDown className="h-4 w-4 mr-1" /> Comprobante
      </Button>
      <Button size="sm" variant="ghost" aria-label="Imprimir comprobante"
        onClick={() => imprimirReporte(path, { tamano: "carta" })}>
        <Printer className="h-4 w-4" />
      </Button>
    </span>
  );
}

function MovimientoDialog({ sitioId, abierto, onClose, tipo, fila, camasLibres, onAplicado, onConflicto }) {
  const [busqueda, setBusqueda] = useState("");
  const [encontradas, setEncontradas] = useState([]);
  const [personaId, setPersonaId] = useState(null);
  const [nueva, setNueva] = useState({ nombre_completo: "", fecha_nacimiento: "", identificador_centro: "" });
  const [modoNueva, setModoNueva] = useState(false);
  const [destino, setDestino] = useState("");
  const [prev, setPrev] = useState(null);
  const [reconocer, setReconocer] = useState(false);
  const [clasif, setClasif] = useState({});
  const [nota, setNota] = useState("");
  const [estado, setEstado] = useState("captura");   // captura | revisar | enviando | incierto | hecho
  const [clave, setClave] = useState(nuevaClave);
  const [resultado, setResultado] = useState(null);
  // A2 · un solo envío en vuelo: el segundo clic (o tecla) no sale aunque React no haya repintado.
  const enVuelo = useRef(false);
  const camaFila = fila?.cama_id;

  useEffect(() => {
    if (!abierto) return;
    // C5 · cama con persona registrada sin adoptar: la adopción es de ESA persona en ESA cama.
    setBusqueda(""); setEncontradas([]);
    setPersonaId(tipo === "ingreso" && !fila?.asignada_sin_adoptar ? null : fila?.persona_id);
    setNueva({ nombre_completo: "", fecha_nacimiento: "", identificador_centro: "" }); setModoNueva(false);
    setDestino(tipo === "ingreso" ? fila?.cama_id : ""); setPrev(null); setReconocer(false); setClasif({});
    setNota(""); setEstado("captura"); setClave(nuevaClave()); setResultado(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, tipo, camaFila]);

  const buscar = async () => {
    try {
      const { data } = await api.get(`/sitios/${sitioId}/personas-busqueda`, { params: { q: busqueda } });
      setEncontradas(data.personas || []);
    } catch (e) { toast.error(detalleError(e)); }
  };

  const cuerpo = useMemo(() => {
    const b = { tipo, nota: nota || null, reconocer_casos_abiertos: reconocer, clasificaciones_ajustes: clasif };
    if (tipo === "ingreso") {
      b.cama_destino_id = fila?.cama_id;
      b.version_cama_destino = fila?.version;
      if (modoNueva) b.persona_nueva = { nombre_completo: nueva.nombre_completo,
        fecha_nacimiento: nueva.fecha_nacimiento || null, identificador_centro: nueva.identificador_centro || null };
      else b.persona_id = personaId;
    } else {
      b.persona_id = fila?.persona_id;
      // B4 · versiones que la pantalla vio: estancia y cama de origen.
      b.version_estancia = fila?.version_estancia;
      b.version_cama_origen = fila?.version;
      if (tipo === "traslado") {
        b.cama_destino_id = destino;
        b.version_cama_destino = (camasLibres.find((c) => c.cama_id === destino) || {}).version;
      }
    }
    return b;
  }, [tipo, nota, reconocer, clasif, fila, modoNueva, nueva, personaId, destino, camasLibres]);

  const listoParaRevisar = tipo === "egreso" || (tipo === "traslado" && destino)
    || (tipo === "ingreso" && (modoNueva ? nueva.nombre_completo.trim().length > 1 : !!personaId));

  const revisar = async () => {
    try {
      const { data } = await api.post(`/sitios/${sitioId}/movimientos/previsualizar`, cuerpo);
      setPrev(data); setEstado("revisar"); setClave(nuevaClave());
    } catch (e) { toast.error(detalleError(e)); }
  };

  const confirmar = async () => {
    if (enVuelo.current) return;
    enVuelo.current = true;
    setEstado("enviando");
    try {
      const { data } = await api.post(`/sitios/${sitioId}/movimientos`, cuerpo, { headers: { "Idempotency-Key": clave } });
      setResultado(data.movimiento); setEstado("hecho");
      toast.success(`${TITULO[tipo]} registrado · folio ${data.movimiento.folio}`);
      onAplicado && onAplicado();
    } catch (e) {
      if (!e?.response || e.response.status >= 500) {
        // Respuesta perdida o servidor caído: NO se sabe si se aplicó. Se ofrece repetir la MISMA
        // operación (misma clave): el servidor devuelve el resultado o la completa, nunca duplica.
        setEstado("incierto");
        return;
      }
      const codigo = codigoError(e);
      if (codigo === "movimiento_en_proceso") {
        // B9 · otra invocación está aplicando ESTA operación: estado incierto, no error.
        setEstado("incierto");
        return;
      }
      toast.error(detalleError(e));
      if (e.response.status === 409 && CONFLICTO_PANTALLA.has(codigo)) {
        // B9 · pantalla vieja: se refresca la fila (versiones vigentes) y se vuelve a revisar.
        setEstado("captura"); setPrev(null);
        onConflicto && onConflicto();
        return;
      }
      setEstado("revisar");
      if (e.response.status === 409) onAplicado && onAplicado();
    } finally {
      enVuelo.current = false;
    }
  };

  const ef = prev?.efectos || {};
  const ambiguos = (ef.ajustes || []).filter((a) => a.clase === "ambiguo");
  const faltaReconocer = tipo === "egreso" && (ef.casos_abiertos || []).length > 0 && !reconocer;
  const faltaClasif = ambiguos.some((a) => !["terminar", "conservar"].includes(clasif[a.regla_id]));

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v && estado !== "enviando") onClose(); }}>
      <DialogContent className="max-w-lg" data-testid={`mov-dialog-${tipo}`}>
        <DialogHeader>
          <DialogTitle>{TITULO[tipo]} · {fila?.habitacion} · cama {fila?.cama_etiqueta}</DialogTitle>
          <DialogDescription>
            {tipo === "egreso" ? "El egreso es operativo; no representa una autorización médica."
              : "Revisa antes de confirmar. La consola guía; el personal decide."}
          </DialogDescription>
        </DialogHeader>

        {estado === "captura" && (
          <div className="space-y-3">
            {tipo === "ingreso" && fila?.asignada_sin_adoptar && (
              <p className="text-sm text-slate-700" data-testid="mov-adopcion">
                Adopción de <b>{fila.persona_nombre}</b> en su cama registrada. La estancia empieza ahora; no se
                inventa una fecha de ingreso anterior.
              </p>
            )}
            {tipo === "ingreso" && !modoNueva && !fila?.asignada_sin_adoptar && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input data-testid="mov-buscar" placeholder="Buscar persona del centro por nombre"
                    value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                  <Button variant="outline" onClick={buscar} disabled={busqueda.trim().length < 2}>Buscar</Button>
                </div>
                <ul className="max-h-48 overflow-auto divide-y" data-testid="mov-resultados">
                  {encontradas.map((p) => (
                    <li key={p.id}>
                      <button type="button" onClick={() => setPersonaId(p.id)}
                        className={`w-full text-left py-2 px-2 rounded ${personaId === p.id ? "bg-cyan-50" : ""}`}>
                        <span className="font-medium">{p.nombre_completo}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          {p.fecha_nacimiento || "sin fecha"} · {p.identificador_centro || "sin identificador"}
                          {p.estancia_actual_id ? " · con estancia abierta" : ""}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <button type="button" className="text-sm underline text-[#0891A6]" onClick={() => setModoNueva(true)}
                  data-testid="mov-persona-nueva">La persona no está registrada: capturar datos mínimos</button>
              </div>
            )}
            {tipo === "ingreso" && modoNueva && (
              <div className="space-y-2" data-testid="mov-form-nueva">
                <Input placeholder="Nombre completo" value={nueva.nombre_completo}
                  onChange={(e) => setNueva({ ...nueva, nombre_completo: e.target.value })} />
                <Input placeholder="Fecha de nacimiento (AAAA-MM-DD, opcional)" value={nueva.fecha_nacimiento}
                  onChange={(e) => setNueva({ ...nueva, fecha_nacimiento: e.target.value })} />
                <Input placeholder="Identificador del centro (opcional)" value={nueva.identificador_centro}
                  onChange={(e) => setNueva({ ...nueva, identificador_centro: e.target.value })} />
                <button type="button" className="text-sm underline text-[#0891A6]" onClick={() => setModoNueva(false)}>
                  Buscar una persona existente</button>
              </div>
            )}
            {tipo === "traslado" && (
              <label className="block text-sm">Cama destino
                <select data-testid="mov-destino" className="mt-1 w-full rounded-lg border px-2 py-2"
                  value={destino} onChange={(e) => setDestino(e.target.value)}>
                  <option value="">Selecciona una cama libre</option>
                  {camasLibres.map((c) => <option key={c.cama_id} value={c.cama_id}>{c.habitacion} · cama {c.cama_etiqueta}</option>)}
                </select>
              </label>
            )}
            <Input placeholder="Nota (opcional)" value={nota} onChange={(e) => setNota(e.target.value)} />
            <div className="flex justify-end">
              <Button data-testid="mov-revisar" onClick={revisar} disabled={!listoParaRevisar}>Revisar</Button>
            </div>
          </div>
        )}

        {(estado === "revisar" || estado === "enviando") && prev && (
          <div className="space-y-3 text-sm" data-testid="mov-confirmacion">
            <dl className="grid grid-cols-3 gap-1">
              <dt className="text-slate-500">Persona</dt><dd className="col-span-2 font-medium">{prev.plan.persona_nombre}</dd>
              <dt className="text-slate-500">Movimiento</dt><dd className="col-span-2">{prev.plan.subtipo}</dd>
              {prev.plan.cama_origen_etiqueta && (<><dt className="text-slate-500">Origen</dt><dd className="col-span-2">cama {prev.plan.cama_origen_etiqueta}</dd></>)}
              {prev.plan.cama_destino_etiqueta && (<><dt className="text-slate-500">Destino</dt><dd className="col-span-2">cama {prev.plan.cama_destino_etiqueta}</dd></>)}
            </dl>
            {(ef.casos_abiertos || []).length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2" data-testid="mov-casos-abiertos">
                <p className="font-semibold text-amber-800">{ef.casos_abiertos.length} caso(s) abierto(s) de esta persona</p>
                <p className="text-xs text-amber-700">{tipo === "egreso" ? "El egreso no los cierra ni los oculta." : "Son de una estancia anterior."}</p>
                {tipo === "egreso" && (
                  <label className="mt-1 flex items-center gap-2 text-xs">
                    <input type="checkbox" data-testid="mov-reconocer" checked={reconocer} onChange={(e) => setReconocer(e.target.checked)} />
                    Los revisé y entiendo que siguen abiertos
                  </label>
                )}
              </div>
            )}
            {(ef.tareas_a_pausar || []).length > 0 && (
              <p className="text-xs text-slate-600" data-testid="mov-tareas">Se pausarán {ef.tareas_a_pausar.length} tarea(s) personal(es) y se anularán sus ocurrencias futuras.</p>
            )}
            {(ef.tareas_pausadas_previas || []).length > 0 && (
              <p className="text-xs text-slate-600">{ef.tareas_pausadas_previas.length} tarea(s) pausada(s) de una estancia anterior: no se reanudan solas.</p>
            )}
            {ambiguos.length > 0 && (
              <div className="rounded-lg border p-2" data-testid="mov-ajustes">
                <p className="font-semibold">Ajustes temporales heredados sin clasificar</p>
                {!prev.puede_clasificar_ajustes && <p className="text-xs text-amber-700">Requiere a un administrador del centro o a un supervisor.</p>}
                {ambiguos.map((a) => (
                  <div key={a.regla_id} className="mt-1 flex items-center justify-between gap-2 text-xs">
                    <span>{a.nombre || a.tipo_evento} · {a.prioridad} · sobre {a.objetivo}</span>
                    {prev.puede_clasificar_ajustes && (
                      <select value={clasif[a.regla_id] || ""} onChange={(e) => setClasif({ ...clasif, [a.regla_id]: e.target.value })}
                        className="rounded border px-1 py-0.5">
                        <option value="">Elegir</option>
                        <option value="terminar">Termina con este movimiento</option>
                        <option value="conservar">Se conserva</option>
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEstado("captura")} disabled={estado === "enviando"}>Volver</Button>
              <Button data-testid="mov-confirmar" onClick={confirmar}
                disabled={estado === "enviando" || faltaReconocer || faltaClasif || (ambiguos.length > 0 && !prev.puede_clasificar_ajustes)}>
                {estado === "enviando" ? "Registrando…" : `Confirmar ${TITULO[tipo].toLowerCase()}`}
              </Button>
            </div>
          </div>
        )}

        {estado === "incierto" && (
          <div className="space-y-2 text-sm" data-testid="mov-incierto">
            <p className="flex items-center gap-1.5 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> No se confirmó si el registro se aplicó</p>
            <p className="text-xs text-slate-600">Reintentar repite la MISMA operación: si ya se aplicó, devuelve su resultado; no crea otra.</p>
            <div className="flex justify-end">
              <Button data-testid="mov-reintentar" onClick={confirmar}><RefreshCw className="h-4 w-4 mr-1" /> Reintentar el mismo registro</Button>
            </div>
          </div>
        )}

        {estado === "hecho" && resultado && (
          <div className="space-y-2 text-sm" data-testid="mov-hecho">
            <p className="font-semibold text-emerald-700">Registrado · folio {resultado.folio}</p>
            <Comprobante sitioId={sitioId} mov={resultado} />
            <div className="flex justify-end"><Button variant="outline" onClick={onClose}>Cerrar</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ID 11 · C2 — alcance del servicio institucional: descripción concreta, borrador/confirmado y quién
// confirmó. Lo confirma el administrador del centro (o supervisor interno). Aviso administrativo: no
// oculta alertas ni bloquea la atención.
function AlcanceInstitucional({ sitioId }) {
  const [d, setD] = useState(null);
  const [texto, setTexto] = useState("");
  const leer = useCallback(async () => {
    try { const { data } = await api.get(`/sitios/${sitioId}/alcance-institucional`); setD(data); setTexto(data.alcance?.descripcion || ""); }
    catch { setD(null); }
  }, [sitioId]);
  useEffect(() => { leer(); }, [leer]);
  if (!d) return null;
  const a = d.alcance;
  const guardar = async () => {
    try { const { data } = await api.put(`/sitios/${sitioId}/alcance-institucional`, { descripcion: texto }); setD(data); toast.success("Alcance guardado como borrador"); }
    catch (e) { toast.error(detalleError(e)); }
  };
  const confirmarAlcance = async () => {
    try { const { data } = await api.post(`/sitios/${sitioId}/alcance-institucional/confirmar`, { version: a?.version }); setD(data); toast.success("Alcance confirmado"); }
    catch (e) { toast.error(detalleError(e)); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm space-y-2" data-testid="alcance-institucional">
      <p className="font-medium text-[#012472]">Alcance del servicio institucional</p>
      {a ? (
        <p className="text-xs text-slate-600" data-testid="alcance-estado">
          {a.estado === "confirmado" ? `Confirmado por ${a.confirmado_por} · ${fmtFecha(a.confirmado_en)}` : "Borrador sin confirmar"}
        </p>
      ) : <p className="text-xs text-amber-700" data-testid="alcance-estado">Sin registrar. Falta describir y confirmar el alcance del servicio.</p>}
      {d.puede_confirmar ? (
        <>
          <textarea data-testid="alcance-texto" className="w-full rounded-lg border px-2 py-2 text-sm" rows={3}
            placeholder="Qué cubre el servicio en este centro (áreas, horarios, equipos, quién atiende y escala)."
            value={texto} onChange={(e) => setTexto(e.target.value)} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={guardar} disabled={!texto.trim()} data-testid="alcance-guardar">Guardar borrador</Button>
            <Button size="sm" onClick={confirmarAlcance} disabled={!a || a.estado === "confirmado" || texto !== (a?.descripcion || "")} data-testid="alcance-confirmar">Confirmar alcance</Button>
          </div>
        </>
      ) : a && <p className="text-xs text-slate-700 whitespace-pre-line">{a.descripcion}</p>}
      <p className="text-[11px] text-slate-400">{d.aviso}</p>
    </div>
  );
}

export default function HabitacionesInstitucional({ sitioId, esSupervisor }) {
  const [censo, setCenso] = useState(null);
  const [hist, setHist] = useState([]);
  const [dlg, setDlg] = useState({ abierto: false, tipo: null, fila: null });
  const [prevalidacion, setPrevalidacion] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      // B9 · el censo pagina de 100 en 100: se siguen todas las páginas (`siguiente`).
      const leerCenso = async () => {
        let pagina = (await api.get(`/sitios/${sitioId}/censo`)).data;
        const camas = [...(pagina.camas || [])];
        for (let i = 0; pagina.siguiente && i < 100; i += 1) {
          pagina = (await api.get(`/sitios/${sitioId}/censo`, { params: { cursor: pagina.siguiente } })).data;
          camas.push(...(pagina.camas || []));
        }
        return { ...pagina, camas, siguiente: null };
      };
      const [c, { data: h }] = await Promise.all([
        leerCenso(), api.get(`/sitios/${sitioId}/movimientos`, { params: { limite: 30 } })]);
      setCenso(c); setHist(h.movimientos || []); setError(null);
      return c.camas;
    } catch (e) { setError(detalleError(e)); return null; }
  }, [sitioId]);

  const refrescarFila = useCallback(async () => {
    const camas = await cargar();
    if (!camas) return;
    setDlg((d) => (d.abierto && d.fila
      ? { ...d, fila: camas.find((c) => c.cama_id === d.fila.cama_id) || d.fila } : d));
  }, [cargar]);

  useEffect(() => { cargar(); }, [cargar]);

  const verPrevalidacion = async () => {
    try { setPrevalidacion((await api.get(`/sitios/${sitioId}/estancias/prevalidacion`)).data); }
    catch (e) { toast.error(detalleError(e)); }
  };
  const habilitar = async (on) => {
    try { setPrevalidacion((await api.post(`/sitios/${sitioId}/estancias/habilitacion`, { habilitar: on })).data); await cargar(); }
    catch (e) { toast.error(detalleError(e)); }
  };

  if (error) return <p className="text-sm text-amber-700" data-testid="habitaciones-error">{error}</p>;
  if (!censo) return <p className="text-sm text-slate-400">Cargando habitaciones…</p>;
  const camas = censo.camas || [];
  const libres = camas.filter((c) => !c.ocupada && !c.movimiento_en_curso && !c.asignada_sin_adoptar);
  const puede = censo.habilitado && censo.puede_gestionar;

  return (
    <div className="space-y-5" data-testid="habitaciones-institucional">
      <AlcanceInstitucional sitioId={sitioId} />
      {!censo.habilitado && censo.gestionado && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="habitaciones-en-pausa">
          <p className="font-medium text-amber-800">Movimientos en pausa en este centro.</p>
          <p className="text-xs text-amber-700">La consulta, la atribución y la atención siguen igual; las operaciones ya aceptadas se completan.</p>
        </div>
      )}
      {!censo.habilitado && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="habitaciones-no-habilitado">
          {!censo.gestionado && <p className="font-medium text-slate-700">Los movimientos de estancia no están habilitados en este centro.</p>}
          {esSupervisor && (
            <div className="mt-2 space-y-2">
              <Button size="sm" variant="outline" onClick={verPrevalidacion} data-testid="habitaciones-prevalidar">Prevalidar adopción</Button>
              {prevalidacion && (
                <div className="text-xs space-y-1" data-testid="habitaciones-prevalidacion">
                  {(prevalidacion.bloqueantes || []).map((b) => <p key={b} className="text-red-700">• {b}</p>)}
                  {(prevalidacion.avisos || []).map((b) => <p key={b} className="text-slate-600">• {b}</p>)}
                  {(prevalidacion.adopciones || []).length > 0 && (
                    <ul className="mt-1" data-testid="habitaciones-adopciones">
                      {prevalidacion.adopciones.map((a) => (
                        <li key={a.persona_id} className={a.lista ? "text-slate-700" : "text-red-700"}>
                          Adopción en su cama: {a.persona_nombre} · cama {a.cama_etiqueta}{a.lista ? "" : " · detenida: lo registrado se contradice"}
                        </li>))}
                    </ul>
                  )}
                  {prevalidacion.puede_habilitar && <Button size="sm" onClick={() => habilitar(true)} data-testid="habitaciones-habilitar">Habilitar movimientos en este centro</Button>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-base font-medium text-[#012472] mb-3 flex items-center gap-2"><BedDouble className="h-5 w-5" /> Habitaciones y camas</h3>
        <div className="divide-y divide-gray-100">
          {camas.map((c) => (
            <div key={c.cama_id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`censo-cama-${c.cama_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700">{c.habitacion} · cama {c.cama_etiqueta}</p>
                {c.ocupada ? (
                  <p className="text-xs text-slate-500">{c.persona_nombre} · {c.fecha_nacimiento || "sin fecha"} · desde {fmtFecha(c.desde)}</p>
                ) : c.asignada_sin_adoptar ? (
                  <p className="text-xs text-slate-600" data-testid={`censo-sin-adoptar-${c.cama_id}`}>{c.persona_nombre} · registrada en esta cama, sin adoptar</p>
                ) : <p className="text-xs text-slate-400">Disponible</p>}
                {c.movimiento_en_curso && <p className="text-xs font-semibold text-amber-700">Movimiento en curso</p>}
              </div>
              {puede && !c.movimiento_en_curso && (
                <div className="flex gap-1.5">
                  {!c.ocupada && !c.asignada_sin_adoptar && <Button size="sm" data-testid={`censo-ingresar-${c.cama_id}`} onClick={() => setDlg({ abierto: true, tipo: "ingreso", fila: c })}><LogIn className="h-4 w-4 mr-1" /> Ingresar</Button>}
                  {c.asignada_sin_adoptar && <Button size="sm" data-testid={`censo-adoptar-${c.cama_id}`} onClick={() => setDlg({ abierto: true, tipo: "ingreso", fila: c })}><LogIn className="h-4 w-4 mr-1" /> Adoptar</Button>}
                  {c.ocupada && <Button size="sm" variant="outline" data-testid={`censo-trasladar-${c.cama_id}`} onClick={() => setDlg({ abierto: true, tipo: "traslado", fila: c })}><ArrowRightLeft className="h-4 w-4 mr-1" /> Trasladar</Button>}
                  {c.ocupada && <Button size="sm" variant="outline" data-testid={`censo-egresar-${c.cama_id}`} onClick={() => setDlg({ abierto: true, tipo: "egreso", fila: c })}><LogOut className="h-4 w-4 mr-1" /> Egresar</Button>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-base font-medium text-[#012472] mb-3">Movimientos recientes</h3>
        {hist.length === 0 ? <p className="text-sm text-slate-400">Sin movimientos registrados.</p> : (
          <ul className="divide-y divide-gray-100" data-testid="movimientos-historial">
            {hist.map((m) => (
              <li key={m.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <b>{m.folio || "pendiente"}</b> · {m.subtipo || m.tipo} · {m.plan?.persona_nombre}
                  {m.estado === "pendiente" && <span className="ml-2 text-xs font-semibold text-amber-700">pendiente de completar</span>}
                </span>
                <span className="flex gap-1.5">
                  {m.estado === "pendiente" && puede && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      try { await api.post(`/sitios/${sitioId}/movimientos/${m.id}/reintentar`); await cargar(); }
                      catch (e) { toast.error(detalleError(e)); }
                    }}>Completar</Button>
                  )}
                  <Comprobante sitioId={sitioId} mov={m} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <MovimientoDialog sitioId={sitioId} abierto={dlg.abierto} tipo={dlg.tipo} fila={dlg.fila}
        camasLibres={libres} onClose={() => setDlg({ abierto: false, tipo: null, fila: null })} onAplicado={cargar}
        onConflicto={refrescarFila} />
    </div>
  );
}
